import { useId, useRef, useState, type KeyboardEvent } from 'react';

import Modal from '@/components/Modal/Modal';
import { cn } from '@/lib/cn';

import HomesSection from './HomesSection';
import IngestionUrlsSection from './IngestionUrlsSection';
import SaunaSection from './SaunaSection';
import SensorsSection from './SensorsSection';
import UnknownSensorsSection from './UnknownSensorsSection';

interface HomeSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

export type HomeSettingsSectionId = 'homes' | 'sensors' | 'sauna' | 'ingestion' | 'unknown';

interface SectionDef {
  id: HomeSettingsSectionId;
  label: string;
}

const SECTIONS: SectionDef[] = [
  { id: 'homes', label: 'Homes' },
  { id: 'sensors', label: 'Sensors' },
  { id: 'sauna', label: 'Sauna' },
  { id: 'ingestion', label: 'Ingestion URLs' },
  { id: 'unknown', label: 'Unknown sensors' },
];

export default function HomeSettingsModal({ open, onClose }: HomeSettingsModalProps) {
  const [section, setSection] = useState<HomeSettingsSectionId>('homes');
  const tabIdPrefix = useId();
  const panelIdPrefix = useId();
  const tabRefs = useRef<Record<HomeSettingsSectionId, HTMLButtonElement | null>>({
    homes: null,
    sensors: null,
    sauna: null,
    ingestion: null,
    unknown: null,
  });

  const tabId = (id: HomeSettingsSectionId) => `${tabIdPrefix}-${id}`;
  const panelId = (id: HomeSettingsSectionId) => `${panelIdPrefix}-${id}`;

  const focusTab = (id: HomeSettingsSectionId) => {
    setSection(id);
    requestAnimationFrame(() => {
      tabRefs.current[id]?.focus();
    });
  };

  const onTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const idx = SECTIONS.findIndex((s) => s.id === section);
    if (idx < 0) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      focusTab(SECTIONS[(idx + 1) % SECTIONS.length]!.id);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusTab(SECTIONS[(idx - 1 + SECTIONS.length) % SECTIONS.length]!.id);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusTab(SECTIONS[0]!.id);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusTab(SECTIONS[SECTIONS.length - 1]!.id);
    }
  };

  const goToUnknown = () => setSection('unknown');

  return (
    <Modal open={open} onClose={onClose} title="Home settings" description="Manage your home, sensors, and ingestion sources." size="md">
      <div className="space-y-5">
        <nav
          role="tablist"
          aria-label="Home settings sections"
          className="-mx-2 flex flex-wrap gap-1 border-b border-(--border-subtle) px-2 pb-2"
        >
          {SECTIONS.map((def) => {
            const isActive = section === def.id;
            return (
              <button
                key={def.id}
                ref={(el) => {
                  tabRefs.current[def.id] = el;
                }}
                role="tab"
                type="button"
                id={tabId(def.id)}
                aria-selected={isActive}
                aria-controls={panelId(def.id)}
                tabIndex={isActive ? 0 : -1}
                onKeyDown={onTabKeyDown}
                onClick={() => setSection(def.id)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-(--accent-primary) focus-visible:outline-none',
                  isActive
                    ? 'bg-(--accent-primary)/15 text-(--text-primary)'
                    : 'text-(--text-secondary) hover:bg-(--bg-surface-hover) hover:text-(--text-primary)',
                )}
              >
                {def.label}
              </button>
            );
          })}
        </nav>

        <div role="tabpanel" id={panelId(section)} aria-labelledby={tabId(section)} tabIndex={0}>
          {section === 'homes' && <HomesSection />}
          {section === 'sensors' && <SensorsSection onGoToUnknown={goToUnknown} />}
          {section === 'sauna' && <SaunaSection />}
          {section === 'ingestion' && <IngestionUrlsSection />}
          {section === 'unknown' && <UnknownSensorsSection />}
        </div>
      </div>
    </Modal>
  );
}
